import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Save, Trash2, Send, Camera, Gauge, Fuel, MessageSquare, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FUEL = ["Full", "3/4", "1/2", "1/4", "Empty"];

const errMsg = (err, fallback) => {
  if (err?.response?.status === 401) return "Session expired — please log out of admin and back in.";
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => `${(x?.loc || []).slice(-1)[0] || ""}: ${x?.msg || ""}`).join("; ") || fallback;
  if (typeof d === "string" && d) return d;
  return err?.response?.status ? `${fallback} (HTTP ${err.response.status})` : `${fallback} — network error, check connection and try again`;
};

function InspectionForm({ bookingId, type, initial, token, onSaved }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [mileage, setMileage] = useState(initial?.mileage || "");
  const [fuel, setFuel] = useState(initial?.fuel_level || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [photos, setPhotos] = useState(initial?.photos || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [texting, setTexting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setMileage(initial?.mileage || ""); setFuel(initial?.fuel_level || "");
    setNotes(initial?.notes || ""); setPhotos(initial?.photos || []);
  }, [initial]);

  const label = type === "pre" ? "Pre-Trip (Pickup)" : "Post-Trip (Return)";

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/inspections/upload-photo`, fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { path: data.path, content_type: data.content_type, tag: "Exterior", note: "" }]);
      }
      toast.success("Photo(s) added");
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setPhoto = (i, key, val) => setPhotos((arr) => arr.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
  const removePhoto = (i) => setPhotos((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/inspections`, { booking_id: bookingId, type, mileage, fuel_level: fuel, notes, photos }, auth);
      toast.success(`${label} inspection saved`);
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err, "Could not save inspection"));
    } finally { setSaving(false); }
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/inspections/${bookingId}/send-report?type=${type}`, {}, auth);
      toast.success(`Condition report sent to ${data.to}`);
    } catch (err) {
      toast.error(errMsg(err, "Could not send report"));
    } finally { setSending(false); }
  };

  const sendSms = async () => {
    setTexting(true);
    try {
      const { data } = await axios.post(`${API}/inspections/${bookingId}/send-sms?type=${type}`, {}, auth);
      toast.success(`Report link texted to ${data.to}`);
    } catch (err) {
      toast.error(errMsg(err, "Could not text report"));
    } finally { setTexting(false); }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4" data-testid={`inspection-form-${type}-${bookingId}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className={`font-semibold text-sm flex items-center gap-1.5 ${type === "pre" ? "text-[#F2B705]" : "text-sky-400"}`}>
          <Camera size={15} /> {label}
        </h4>
        <span className="flex items-center gap-2">
          {initial?.signoff && (
            <span className="flex items-center gap-1 text-[10px] text-emerald-400 font-semibold" data-testid={`inspection-signed-${type}-${bookingId}`}
              title={`Confirmed by ${initial.signoff.name} on ${new Date(initial.signoff.at).toLocaleString()}`}>
              <CheckCircle2 size={11} /> Renter confirmed
            </span>
          )}
          {initial?.updated_at && <span className="text-[10px] text-white/30">saved</span>}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><Gauge size={11} /> Mileage</label>
          <input data-testid={`inspection-mileage-${type}-${bookingId}`} value={mileage} onChange={(e) => setMileage(e.target.value)} placeholder="Odometer reading"
            className="mt-1 w-full px-2.5 py-1.5 rounded bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><Fuel size={11} /> Fuel</label>
          <select data-testid={`inspection-fuel-${type}-${bookingId}`} value={fuel} onChange={(e) => setFuel(e.target.value)}
            className="mt-1 w-full px-2.5 py-1.5 rounded bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]">
            <option value="">—</option>
            {FUEL.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>

      {/* Photos */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        {photos.map((p, i) => (
          <div key={i} className="relative rounded overflow-hidden border border-white/10 group">
            <img src={`${API}/files/${p.path}?auth=${token}`} alt={p.tag} className="w-full h-20 object-cover" />
            <select value={p.tag} onChange={(e) => setPhoto(i, "tag", e.target.value)}
              className="absolute bottom-0 left-0 right-0 text-[10px] bg-black/80 text-white px-1 py-0.5 outline-none border-0">
              <option value="Exterior">Exterior</option>
              <option value="Interior">Interior</option>
            </select>
            <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/70 rounded p-0.5 text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
          </div>
        ))}
        <label data-testid={`inspection-add-photo-${type}-${bookingId}`} className="flex flex-col items-center justify-center gap-1 h-20 rounded border-2 border-dashed border-white/15 hover:border-[#F2B705]/50 cursor-pointer text-white/40 text-[10px]">
          {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
          Add photos
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
        </label>
      </div>

      <textarea data-testid={`inspection-notes-${type}-${bookingId}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
        placeholder="Condition notes, existing damage, scratches, etc."
        className="w-full px-2.5 py-2 rounded bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705] resize-none" />

      <div className="flex gap-2 mt-3">
        <button data-testid={`inspection-save-${type}-${bookingId}`} onClick={save} disabled={saving}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-[#F2B705] text-black text-sm font-bold disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
        </button>
        <button data-testid={`inspection-send-${type}-${bookingId}`} onClick={sendReport} disabled={sending || !initial}
          title={!initial ? "Save the inspection first" : "Email condition report to customer"}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded border border-white/15 text-white/80 text-sm disabled:opacity-40 hover:bg-white/10">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Email
        </button>
        <button data-testid={`inspection-text-${type}-${bookingId}`} onClick={sendSms} disabled={texting || !initial}
          title={!initial ? "Save the inspection first" : "Text the renter a link to view & confirm"}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded border border-white/15 text-white/80 text-sm disabled:opacity-40 hover:bg-white/10">
          {texting ? <Loader2 className="animate-spin" size={14} /> : <MessageSquare size={14} />} Text
        </button>
      </div>
    </div>
  );
}

export default function InspectionPanel({ bookingId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/inspections/${bookingId}`, auth);
      setData(data);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);

  if (loading) return <div className="py-4 text-white/40 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading inspection…</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`inspection-panel-${bookingId}`}>
      <InspectionForm bookingId={bookingId} type="pre" initial={data?.pre} token={token} onSaved={load} />
      <InspectionForm bookingId={bookingId} type="post" initial={data?.post} token={token} onSaved={load} />
    </div>
  );
}
